import {Popover, Tab, Transition} from "@headlessui/react";
import React, {FC, Fragment, useEffect, useState} from "react";
import Avatar from "shared/Avatar/Avatar";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import {Helmet} from "react-helmet-async";
import {CountryType, OrderEventType, UserType} from "data/types";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import {isProfileCompleted} from "utils/isProfileCompleted";
import {ChevronDownIcon} from "@heroicons/react/24/outline";
import {
  getCountries,
  getCountryByDialCode,
  getCountryByName,
} from "utils/getCountries";
import {
  useLazyValidateUsernameQuery,
  useUpdateImgMutation,
  useUpdateUserMutation,
} from "store/api/usersApi";
import {
  useGetOrderEventByCustomerQuery,
  useGetSubscriptionsByCustomerQuery,
} from "store/api";
import OrderEventCard from "components/OrderEventCard/OrderEventCard";
import {Loading} from "components/Loading/Loading";
import ButtonThird from "shared/Button/ButtonThird";
import NcModal from "shared/NcModal/NcModal";
import {MyCards} from "components/Nuvei/Cards";

export interface AuthorPageProps {
  className?: string;
}

const ProfilePage: FC<AuthorPageProps> = ({className = ""}) => {
  let [categories] = useState(["Tickets", "Carreras", "Pedidos"]);
  let [eventCategories] = useState(["Próximos", "Historial", "Abonos"]);
  const [updateUser] = useUpdateUserMutation();
  const [updateImg] = useUpdateImgMutation();
  const [categoryState, setCategoryState] = useState("Próximos");
  // const [isRecord, setIsRecord] = useState(false);

  const documentType = [
    {value: "CI", viewValue: "CI"},
    {value: "CE", viewValue: "CE"},
    {value: "DNI", viewValue: "DNI"},
    {value: "PASSPORT", viewValue: "PASAPORTE"},
    {value: "RUC", viewValue: "RUC"},
    {value: "NIT", viewValue: "NIT"},
  ];

  const genders = [
    {value: "MALE", viewValue: "Masculino"},
    {value: "FEMALE", viewValue: "Femenino"},
    {value: "TRANSGENDER", viewValue: "Transgénero"},
    {value: "NON BINARY", viewValue: "No binario"},
    {value: "PREFER NO TO SAY", viewValue: "Prefiero no decirlo"},
    {value: "OTHER", viewValue: "Otro"},
  ];

  const userData = localStorage.getItem("user");
  let user: UserType;
  if (userData) {
    user = JSON.parse(userData) as UserType;
  }
  const {
    data: upcomingData,
    isLoading: isLoadingOrders,
    isError: isErrorOrders,
    error: errorOrders,
  } = useGetOrderEventByCustomerQuery(false);
  const {
    data: pastData,
    isLoading: isLoadingPastOrders,
    isError: isErrorPastOrders,
    error: errorPastOrders,
  } = useGetOrderEventByCustomerQuery(true);
  const {data: subscriptionsData} = useGetSubscriptionsByCustomerQuery({});

  const [documentTypeState, setDocumentTypeState] = useState("Documento");
  const [ciState, setCiState] = useState("");
  const [usernameState, setUsernameState] = useState("");
  const [addressState, setAddressState] = useState("");
  const [genderState, setGenderState] = useState("Género");
  const [countryState, setCountryState] = useState("País");
  const [prefixState, setPrefixState] = useState("Prefijo");
  const [phoneState, setPhoneState] = useState("");
  const [prefix1State, setPrefix1State] = useState("Prefijo");
  const [phone1State, setPhone1State] = useState("");
  const [prefix2State, setPrefix2State] = useState("Prefijo");
  const [phone2State, setPhone2State] = useState("");
  const [prefix3State, setPrefix3State] = useState("Prefijo");
  const [phone3State, setPhone3State] = useState("");
  const [phoneMaxLength, setPhoneMaxLength] = useState<number>(9);
  const [phoneMaxLength1, setPhoneMaxLength1] = useState<number>(9);
  const [phoneMaxLength2, setPhoneMaxLength2] = useState<number>(9);
  const [phoneMaxLength3, setPhoneMaxLength3] = useState<number>(9);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [usernameExists, setUsernameExists] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const countries: CountryType[] = getCountries();
  const [birthdate, setBirthdate] = useState("");
  const [birthdateLabel, setBirthdateLabel] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdatingImg, setIsUpdatingImg] = useState(false);
  const [img, setImg] = useState("");
  const [orderEvents, setOrderEvents] = useState<OrderEventType[]>();
  const [orderPastEvents, setOrderPastEvents] = useState<OrderEventType[]>();
  const [orderSubscriptions, setOrderSubscriptions] = useState<any[]>();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = e.target.value;
    setBirthdate(newDate);
  };

  useEffect(() => {
    setImg(user.img);
    setDocumentTypeState(
      user.document === ""
        ? "Documento"
        : getViewValue(user.document, documentType),
    );
    setGenderState(
      user.gender === "" ? "Género" : getViewValue(user.gender, genders),
    );
    setUsernameState(user.username ?? "");
    setCiState(user.ci);
    setAddressState(user.address);
    const date = new Date(user.birthdate);
    const dateFormatted = date.toISOString();
    const day = String(date.getUTCDate()).padStart(2, "0");
    const month = String(date.getUTCMonth() + 1).padStart(2, "0"); // Meses base 0
    const year = date.getUTCFullYear();
    setBirthdateLabel(`${day}/${month}/${year}`);
    setBirthdate(dateFormatted);
    const country = getCountryByName(user.country);
    setCountryState(country?.name ?? "País");
    setPhoneState(user.phone);
    const prefix = user.prefix.replace("+", "") || "593";
    setPrefixState(prefix);
    setPrefix1State(user?.phoneEmergency[0]?.prefix.replace("+", "") || "593");
    setPhone1State(user?.phoneEmergency[0]?.phone || "");
    setPrefix2State(user?.phoneEmergency[1]?.prefix?.replace("+", "") || "593");
    setPhone2State(user?.phoneEmergency[1]?.phone || "");
    setPrefix3State(user?.phoneEmergency[2]?.prefix?.replace("+", "") || "593");
    setPhone3State(user?.phoneEmergency[2]?.phone || "");
  }, []);

  const [errorMessage, setErrorMessage] = useState<string>("");

  useEffect(() => {
    if (
      isErrorOrders &&
      errorOrders &&
      "status" in errorOrders &&
      errorOrders.status === 404
    ) {
      setOrderEvents([]);
    } else if (upcomingData) {
      const orders = upcomingData.data ?? [];
      setOrderEvents(orders);
    }

    if (
      isErrorPastOrders &&
      errorPastOrders &&
      "status" in errorPastOrders &&
      errorPastOrders.status === 404
    ) {
      setOrderPastEvents([]);
    } else if (pastData) {
      const orders = pastData.data;
      setOrderPastEvents(orders);
    }
    if (subscriptionsData) {
      const orders = subscriptionsData.data;
      setOrderSubscriptions(orders);
    }
  }, [
    isLoadingOrders,
    isErrorOrders,
    errorOrders,
    categoryState,
    upcomingData,
    pastData,
    subscriptionsData,
  ]);

  const [validateUsername] = useLazyValidateUsernameQuery();

  const getViewValue = (value: string, categories: any[]): string => {
    const found = categories.find((item) => item.value === value);
    return found ? found.viewValue : undefined;
  };

  const getValue = (viewValue: string, categories: any[]): string => {
    const found = categories.find((item) => item.viewValue === viewValue);
    return found ? found.value : undefined;
  };

  const submitHandler = async () => {
    if (!isEditing) {
      setIsEditing(true);
    } else {
      if (
        usernameState.length < 4 ||
        ciState === "" ||
        addressState === "" ||
        countryState === "País" ||
        genderState === "Género" ||
        documentTypeState === "Documento" ||
        phoneMaxLength !== phoneState.length ||
        phoneMaxLength1 !== phone1State.length
      ) {
        setHasInteracted(true);
        return;
      } else {
        setHasInteracted(false);
        setIsLoading(true);
        try {
          const {data, error} = await validateUsername(usernameState);
          if (data) {
            if (data.meta.status === 200 && !user?.username) {
              setUsernameExists(true);
              setIsLoading(false);
              return;
            }
          }
          if (error || user?.username) {
            setUsernameExists(false);
            const phoneEmergency = [
              {prefix: `+${prefix1State}`, phone: phone1State},
              phone2State && {prefix: `+${prefix2State}`, phone: phone2State},
              phone3State && {prefix: `+${prefix3State}`, phone: phone3State},
            ].filter(Boolean);

            const body = {
              _id: user._id,
              document: getValue(documentTypeState, documentType),
              ci: ciState,
              name: user.name,
              username: usernameState,
              email: user.email,
              gender: getValue(genderState, genders),
              birthdate: birthdate,
              country: countryState.toUpperCase(),
              prefix: `+${prefixState}`,
              phone: phoneState,
              address: addressState,
              phoneEmergency: phoneEmergency,
            };
            const {data: userData, error: userError} = await updateUser(body);
            if (userData) {
              if (userData.meta.status === 200) {
                const userDB = {...userData.customer};
                delete userDB.img;
                const newUser = {...user, ...userDB};
                localStorage.setItem("user", JSON.stringify(newUser));
                const date = new Date(birthdate);
                const day = String(date.getUTCDate()).padStart(2, "0");
                const month = String(date.getUTCMonth() + 1).padStart(2, "0");
                const year = date.getUTCFullYear();
                setBirthdateLabel(`${day}/${month}/${year}`);
                setIsEditing(false);
                setIsLoading(false);
              } 
            }
            if (userError) {
              setIsLoading(false);
              setErrorMessage(
                "Otro usuario ya está utilizando este número de teléfono o número de identificación",
              );
              setTimeout(() => setErrorMessage(""), 3000);
            }
              
          }
        } catch (error) {
          console.error(error);
        }
      }
    }
  };

  const handleImgChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validImageTypes = ["image/jpeg", "image/png", "image/jpg"];
    if (!validImageTypes.includes(file.type)) {
      alert("Por favor selecciona una imagen válida (JPEG, PNG, JPG)");
      return;
    }

    const reader = new FileReader();
    reader.onload = async () => {
      if (reader.result) {
        const img = new Image();
        img.src = reader.result as string;

        img.onload = async () => {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");

          const maxWidth = 400;
          const maxHeight = 400;
          let width = img.width;
          let height = img.height;

          if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          }

          if (height > maxHeight) {
            width = Math.round((width * maxHeight) / height);
            height = maxHeight;
          }

          canvas.width = width;
          canvas.height = height;

          ctx?.drawImage(img, 0, 0, width, height);

          const compressedBase64 = canvas.toDataURL("image/jpeg", 0.7);

          const base64WithoutHeader = compressedBase64.split(",")[1];
          const body = {
            _id: user._id,
            img: base64WithoutHeader,
          };

          setIsUpdatingImg(true);
          const {data} = await updateImg(body);
          setIsUpdatingImg(false);

          if (data) {
            if (data.meta.status === 200) {
              const userDB = {...data.customer};
              delete userDB._id;
              setImg(`${userDB.img}?timestamp=${new Date().getTime()}`);
              const newUser = {...user, ...userDB};
              localStorage.setItem("user", JSON.stringify(newUser));
            }
          }
        };
      }
    };

    reader.onerror = (error) => {
      console.error("Error al leer el archivo:", error);
    };

    try {
      reader.readAsDataURL(file);
    } catch (error) {
      console.error("Error al intentar leer el archivo:", error);
    }
  };

  const renderSidebar = () => {
    return (
      <div
        className={` w-full flex flex-col items-center text-center sm:rounded-2xl sm:border border-neutral-200 dark:border-neutral-700 space-y-${
          isEditing ? "2" : "6"
        } sm:space-y-${isEditing ? "2" : "7"} px-0 sm:p-6 xl:p-8`}
      >
        <label
          htmlFor="file-upload"
          className="relative cursor-pointer  rounded-md font-medium text-primary-6000 hover:text-primary-500 focus-within:outline-none"
        >
          <div className="relative w-28 h-28">
            {isUpdatingImg ? (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-100 bg-opacity-50 rounded-full z-10">
                <div className="w-6 h-6 border-4 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : null}
            <Avatar
              hasCheckedClass="w-6 h-6 -top-0.5 right-2"
              sizeClass="w-28 h-28"
              imgUrl={img}
            />
          </div>
          <input
            id="file-upload"
            name="file-upload"
            type="file"
            accept="image/*"
            className="sr-only"
            onChange={handleImgChange}
          />
        </label>

        <div className="space-y-3 text-center flex flex-col items-center">
          <h2 className="text-3xl font-semibold">{user.name}</h2>
          <p className="text-neutral-500 dark:text-neutral-400">
            @{user.username}
          </p>
        </div>

        <div className="flex items-center space-x-4 w-full justify-center">
          <i className="las la-envelope flex-shrink-0"></i>
          <p className="text-neutral-500 dark:text-neutral-200 text-sm leading-none">
            {user.email}
          </p>
          {/* <input type="text" className="border-none" value={user.email}/> */}
        </div>
        {isEditing && (
          <div className="flex items-center space-x-4 w-full justify-center">
            <Popover className="relative">
              {({open, close}) => (
                <>
                  <Popover.Button
                    className={`
           ${open ? "" : ""}
            px-4 py-1.5 rounded-md inline-flex items-center font-medium hover:text-opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 text-xs`}
                    onClickCapture={() =>
                      document.querySelector("html")?.click()
                    }
                  >
                    <span>{`${documentTypeState}`}</span>
                    <ChevronDownIcon
                      className={`${
                        open ? "" : "text-opacity-70"
                      } ml-2 h-4 w-4 group-hover:text-opacity-80 transition ease-in-out duration-150`}
                      aria-hidden="true"
                    />
                  </Popover.Button>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-200"
                    enterFrom="opacity-0 translate-y-1"
                    enterTo="opacity-100 translate-y-0"
                    leave="transition ease-in duration-150"
                    leaveFrom="opacity-100 translate-y-0"
                    leaveTo="opacity-0 translate-y-1"
                  >
                    <Popover.Panel className="absolute z-20 w-screen max-w-[200px] sm:max-w-[220px] px-4 top-full mt-3 transform -translate-x-1/2 left-1/2 sm:px-0  ">
                      <div className="overflow-hidden rounded-2xl shadow-lg ring-1 ring-black/5 dark:ring-white/10 ">
                        <div className="relative grid gap-8 bg-white dark:bg-neutral-800 p-7 ">
                          {documentType.map((item: any) => (
                            <a
                              key={item.value}
                              href={item.value}
                              onClick={(e) => {
                                e.preventDefault();
                                setDocumentTypeState(item.viewValue);
                                close();
                              }}
                              className="flex items-center p-2 -m-3 transition duration-150 ease-in-out rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus-visible:ring focus-visible:ring-orange-500 focus-visible:ring-opacity-50"
                            >
                              <p className="text-sm font-medium ">
                                {item.viewValue}
                              </p>
                            </a>
                          ))}
                        </div>
                      </div>
                    </Popover.Panel>
                  </Transition>
                </>
              )}
            </Popover>
            {hasInteracted && documentTypeState === "Documento" && (
              <p className="text-red-500 text-xs">{"*"}</p>
            )}

            <Popover className="relative">
              {({open, close}) => (
                <>
                  <Popover.Button
                    className={`
           ${open ? "" : ""}
            px-4 py-1.5 rounded-md inline-flex items-center font-medium hover:text-opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 text-xs`}
                    onClickCapture={() =>
                      document.querySelector("html")?.click()
                    }
                  >
                    <span>{`${genderState}`}</span>
                    <ChevronDownIcon
                      className={`${
                        open ? "" : "text-opacity-70"
                      } ml-2 h-4 w-4 group-hover:text-opacity-80 transition ease-in-out duration-150`}
                      aria-hidden="true"
                    />
                  </Popover.Button>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-200"
                    enterFrom="opacity-0 translate-y-1"
                    enterTo="opacity-100 translate-y-0"
                    leave="transition ease-in duration-150"
                    leaveFrom="opacity-100 translate-y-0"
                    leaveTo="opacity-0 translate-y-1"
                  >
                    <Popover.Panel className="absolute z-20 w-screen max-w-[200px] sm:max-w-[220px] px-4 top-full mt-3 transform -translate-x-1/2 left-1/2 sm:px-0  ">
                      <div className="overflow-hidden rounded-2xl shadow-lg ring-1 ring-black/5 dark:ring-white/10 ">
                        <div className="relative grid gap-8 bg-white dark:bg-neutral-800 p-7 ">
                          {genders.map((item: any) => (
                            <a
                              key={item.value}
                              href={item.value}
                              onClick={(e) => {
                                e.preventDefault();
                                setGenderState(item.viewValue);
                                close();
                              }}
                              className="flex items-center p-2 -m-3 transition duration-150 ease-in-out rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus-visible:ring focus-visible:ring-orange-500 focus-visible:ring-opacity-50"
                            >
                              <p className="text-sm font-medium ">
                                {item.viewValue}
                              </p>
                            </a>
                          ))}
                        </div>
                      </div>
                    </Popover.Panel>
                  </Transition>
                </>
              )}
            </Popover>
            {hasInteracted && genderState === "Género" && (
              <p className="text-red-500 text-xs">{"*"}</p>
            )}
          </div>
        )}
        {isEditing && !user?.username && (
          <>
            <div className="flex items-center space-x-4 w-full justify-center">
              <i className="las la-user flex-shrink-0"></i>
              <input
                type="text"
                value={usernameState}
                placeholder="Username"
                maxLength={20}
                minLength={4}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^[a-z0-9._-]*$/.test(value)) setUsernameState(value);
                }}
              />
            </div>
            {hasInteracted && usernameState.length < 4 && (
              <p className="text-red-500 text-xs">
                {
                  "Mínimo 4 caracteres, máximo 20. Solo minúsculas, números, guiones y puntos"
                }
              </p>
            )}
            {usernameExists && (
              <p className="text-red-500 text-xs">
                {"Este username ya se encuentra registrado por otro usuario"}
              </p>
            )}
            <p className="text-primary-500 text-xs">
              {"Una ves registrado, el username no puede ser cambiado"}
            </p>
          </>
        )}
        <div className="flex items-center space-x-4 w-full justify-center">
          {(user.ci !== "" || isEditing) && (
            <i className="las la-id-card flex-shrink-0"></i>
          )}
          {isEditing ? (
            <input
              type="text"
              value={ciState}
              placeholder="Número de documento"
              onChange={(e) => setCiState(e.target.value)}
            />
          ) : (
            <p className="text-neutral-500 dark:text-neutral-200 text-sm leading-none">
              {user.ci}
            </p>
          )}

          {hasInteracted && ciState === "" && (
            <p className="text-red-500 text-xs">{"*"}</p>
          )}
        </div>
        <div className="flex items-center space-x-4 w-full justify-center">
          {(user.address !== "" || isEditing) && (
            <i className="las la-map-marker flex-shrink-0"></i>
          )}
          {isEditing ? (
            <input
              type="text"
              value={addressState}
              placeholder="Dirección"
              onChange={(e) => setAddressState(e.target.value)}
            />
          ) : (
            user.address !== "" && (
              <p className="text-neutral-500 dark:text-neutral-200 text-sm leading-none">
                {user.address}
              </p>
            )
          )}
          {hasInteracted && ciState === "" && (
            <p className="text-red-500 text-xs">{"*"}</p>
          )}
        </div>
        <div className="flex items-center space-x-4 w-full justify-center">
          {(user.birthdate !== null || isEditing) && (
            <i className="las la-calendar flex-shrink-0"></i>
          )}
          {isEditing ? (
            <input
              type="date"
              onChange={handleInputChange}
              max={new Date().toISOString().split("T")[0]}
              value={new Date(birthdate).toISOString().split("T")[0]}
              onClick={(e) => {
                const input = e.target as HTMLInputElement;
                if (input.showPicker) {
                  input.showPicker();
                }
              }}
            />
          ) : (
            user.birthdate !== null && (
              <p className="text-neutral-500 dark:text-neutral-200 text-sm leading-none">
                {birthdateLabel}
              </p>
            )
          )}
        </div>

        <div className="flex items-center space-x-4 w-full justify-center">
          {(user.country !== "" || isEditing) && (
            <i className="las la-flag flex-shrink-0"></i>
          )}
          {isEditing ? (
            <select
              value={countryState}
              onChange={(e) => setCountryState(e.target.value)}
            >
              <option value="País" disabled>
                País
              </option>
              {countries.map((country) => (
                <option key={country.code} value={country.name}>
                  {country.name}
                </option>
              ))}
            </select>
          ) : (
            <p className="text-neutral-500 dark:text-neutral-200 text-sm leading-none">
              {user.country}
            </p>
          )}
          {hasInteracted && countryState === "País" && (
            <p className="text-red-500 text-xs">{"*"}</p>
          )}
        </div>
        <div className="flex items-center space-x-4 w-full justify-center">
          {isEditing ? (
            <>
              <i className="las la-flag flex-shrink-0"></i>
              <select
                key="prefix"
                value={prefixState}
                onChange={(e) => {
                  setPrefixState(e.target.value);
                  const country = getCountryByDialCode(e.target.value);
                  setPhoneMaxLength(country!.maxLength);
                  setPhoneState("");
                }}
              >
                <option value="Prefijo" disabled>
                  Prefijo
                </option>
                {countries.map((country) => (
                  <option
                    key={country.dialCode + country.name}
                    value={country.dialCode}
                  >
                    {country.flag} +{country.dialCode} {country.name}
                  </option>
                ))}
              </select>
              <i className="las la-phone flex-shrink-0"></i>
              <input
                type="text"
                value={phoneState}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d*$/.test(value)) setPhoneState(value);
                }}
                maxLength={phoneMaxLength}
                inputMode="numeric"
                pattern="\d*"
              />
            </>
          ) : (
            user.phone !== "" && (
              <>
                <i className="las la-phone flex-shrink-0"></i>
                <p className="text-neutral-500 dark:text-neutral-200 text-sm leading-none">
                  {user.phone}
                </p>
              </>
            )
          )}
        </div>
        {hasInteracted && isEditing && phoneMaxLength !== phoneState.length && (
          <p className="text-red-500 text-xs">{"El número no es válido"}</p>
        )}
        <div className="border-b border-neutral-200 dark:border-neutral-700 w-14"></div>

        {isEditing && (
          <div className="space-y-3 text-center flex flex-col items-center">
            <p className="text-neutral-500 dark:text-neutral-400">
              CONTACTOS DE EMERGENCIA
            </p>
            <p className="text-neutral-500 dark:text-neutral-400">
              Al menos un contacto de emergencia
            </p>
            <div className="flex items-center space-x-4 w-full justify-center">
              <i className="las la-flag flex-shrink-0"></i>
              <select
                value={prefix1State}
                onChange={(e) => {
                  setPrefix1State(e.target.value);
                  const country = getCountryByDialCode(e.target.value);
                  setPhoneMaxLength1(country!.maxLength);
                  setPhone1State("");
                }}
              >
                <option value="Prefijo" disabled>
                  Prefijo
                </option>
                {countries.map((country) => (
                  <option
                    key={country.dialCode + country.name}
                    value={country.dialCode}
                  >
                    {country.flag} +{country.dialCode} {country.name}
                  </option>
                ))}
              </select>
              <i className="las la-phone flex-shrink-0"></i>
              <input
                type="text"
                value={phone1State}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d*$/.test(value)) setPhone1State(value);
                }}
                maxLength={phoneMaxLength1}
                inputMode="numeric"
                pattern="\d*"
              />
            </div>
            {hasInteracted &&
              isEditing &&
              phoneMaxLength1 !== phone1State.length && (
                <p className="text-red-500 text-xs">
                  {"El número no es válido"}
                </p>
              )}
            <div className="flex items-center space-x-4 w-full justify-center">
              <i className="las la-flag flex-shrink-0"></i>
              <select
                value={prefix2State}
                onChange={(e) => {
                  setPrefix2State(e.target.value);
                  const country = getCountryByDialCode(e.target.value);
                  setPhoneMaxLength2(country!.maxLength);
                  setPhone2State("");
                }}
              >
                <option value="Prefijo" disabled>
                  Prefijo
                </option>
                {countries.map((country) => (
                  <option
                    key={country.dialCode + country.name}
                    value={country.dialCode}
                  >
                    {country.flag} +{country.dialCode} {country.name}
                  </option>
                ))}
              </select>
              <i className="las la-phone flex-shrink-0"></i>
              <input
                type="text"
                value={phone2State}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d*$/.test(value)) setPhone2State(value);
                }}
                maxLength={phoneMaxLength2}
                inputMode="numeric"
                pattern="\d*"
              />
            </div>
            <div className="flex items-center space-x-4 w-full justify-center">
              <i className="las la-flag flex-shrink-0"></i>
              <select
                value={prefix3State}
                onChange={(e) => {
                  setPrefix3State(e.target.value);
                  const country = getCountryByDialCode(e.target.value);
                  setPhoneMaxLength3(country!.maxLength);
                  setPhone3State("");
                }}
              >
                <option value="Prefijo" disabled>
                  Prefijo
                </option>
                {countries.map((country) => (
                  <option
                    key={country.dialCode + country.name}
                    value={country.dialCode}
                  >
                    {country.flag} +{country.dialCode} {country.name}
                  </option>
                ))}
              </select>
              <i className="las la-phone flex-shrink-0"></i>
              <input
                type="text"
                value={phone3State}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d*$/.test(value)) setPhone3State(value);
                }}
                maxLength={phoneMaxLength3}
                inputMode="numeric"
                pattern="\d*"
              />
            </div>
          </div>
        )}
        <NcModal
          contentExtraClass="max-w-3xl"
          renderTrigger={(openModal) => (
            <ButtonThird fontSize="text-xs" onClick={() => openModal()}>
              Ver mis tarjetas
            </ButtonThird>
          )}
          modalTitle="Mis tarjetas"
          renderContent={() => <MyCards />}
        />
        <div className="mt-16 flex flex-col sm:flex-row justify-center space-y-3 sm:space-y-0 sm:space-x-5 text-xs">
          {isEditing && (
            <ButtonSecondary onClick={() => setIsEditing(false)}>
              Cancelar
            </ButtonSecondary>
          )}
          <ButtonPrimary loading={isLoading} onClick={submitHandler}>
            {isEditing
              ? "Guardar"
              : isProfileCompleted()
                ? "Editar perfil"
                : "Completar perfil"}
          </ButtonPrimary>
        </div>
      </div>
    );
  };

  const renderSelectCategory = () => {
    return (
      <Popover className="relative">
        {({open, close}) => (
          <>
            <Popover.Button
              className={`
              ${open ? "" : ""}
              px-4 py-1.5 rounded-md inline-flex items-center font-medium hover:text-opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 text-sm`}
              onClickCapture={() => document.querySelector("html")?.click()}
            >
              <span>{`${categoryState}`}</span>
              <ChevronDownIcon
                className={`${
                  open ? "" : "text-opacity-70"
                } ml-2 h-4 w-4 group-hover:text-opacity-80 transition ease-in-out duration-150`}
                aria-hidden="true"
              />
            </Popover.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <Popover.Panel className="absolute z-20 w-screen max-w-[200px] sm:max-w-[220px] px-4 top-full mt-3 transform -translate-x-1/2 left-1/2 sm:px-0  ">
                <div className="overflow-hidden rounded-2xl shadow-lg ring-1 ring-black/5 dark:ring-white/10 ">
                  <div className="relative grid gap-8 bg-white dark:bg-neutral-800 p-7 ">
                    {eventCategories.map((item: any) => (
                      <a
                        key={item}
                        href={item}
                        onClick={(e) => {
                          e.preventDefault();

                          setCategoryState(item);
                          close();
                        }}
                        className="flex items-center p-2 -m-3 transition duration-150 ease-in-out rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus-visible:ring focus-visible:ring-orange-500 focus-visible:ring-opacity-50"
                      >
                        <p className="text-sm font-medium ">{item}</p>
                      </a>
                    ))}
                  </div>
                </div>
              </Popover.Panel>
            </Transition>
          </>
        )}
      </Popover>
    );
  };

  const renderPurchases = () => {
    return (
      <div className="listingSection__wrap">
        <div>
          <Tab.Group>
            <Tab.List className="flex justify-center space-x-1 overflow-x-auto">
              {categories.map((item) => (
                <Tab key={item} as={Fragment}>
                  {({selected}) => (
                    <button
                      className={`flex-shrink-0 block !leading-none font-medium px-5 py-2.5 text-sm sm:text-base sm:px-6 sm:py-3 capitalize rounded-full focus:outline-none ${
                        selected
                          ? "bg-primary-700 text-secondary-50 "
                          : "text-neutral-500 dark:text-neutral-400 dark:hover:text-neutral-100 hover:text-neutral-900 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                      } `}
                    >
                      {item}
                    </button>
                  )}
                </Tab>
              ))}
            </Tab.List>
            <Tab.Panels>
              <Tab.Panel className="">
                <div className="flex justify-center mt-4">
                  <div className="mr-2 my-1 sm:mr-3 border border-neutral-300 dark:border-neutral-700 rounded-full">
                    {renderSelectCategory()}
                  </div>
                </div>

                {categoryState === "Próximos" && (
                  <>
                    {!orderEvents ? (
                      <Loading />
                    ) : orderEvents.length === 0 ? (
                      <span className="mt-8 flex justify-center">
                        No se encontraron próximos eventos
                      </span>
                    ) : (
                      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 md:gap-7">
                        {orderEvents.map((order: OrderEventType) => (
                          <OrderEventCard
                            key={order._id}
                            order={order}
                            isRaffle={order.event.category === "Sorteo"}
                          />
                        ))}
                      </div>
                    )}
                  </>
                )}
                {categoryState === "Historial" && (
                  <>
                    {!orderPastEvents ? (
                      <Loading />
                    ) : orderPastEvents.length === 0 ? (
                      <span className="mt-8 flex justify-center">
                        No se encontraron eventos
                      </span>
                    ) : (
                      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 md:gap-7">
                        {orderPastEvents.map((order: OrderEventType) => (
                          <OrderEventCard
                            key={order._id}
                            order={order}
                            isRaffle={order.event.category === "Sorteo"}
                            isPastEvent={true}
                          />
                        ))}
                      </div>
                    )}
                  </>
                )}
                {categoryState === "Abonos" && (
                  <>
                    {!orderSubscriptions ? (
                      <Loading />
                    ) : orderSubscriptions.length === 0 ? (
                      <span className="mt-8 flex justify-center">
                        No se encontraron abonos
                      </span>
                    ) : (
                      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 md:gap-7">
                        {orderSubscriptions.map((order: any) => (
                          <OrderEventCard
                            key={order._id}
                            order={order}
                            isSubscriber={true}
                            isRaffle={false}
                            subscriberImg={order.qrCode}
                          />
                        ))}
                      </div>
                    )}
                  </>
                )}
              </Tab.Panel>
              <Tab.Panel className="">
                <div className="mt-8 grid grid-cols-1 gap-6 md:gap-7 sm:grid-cols-2">
                  {/* {DEMO_EXPERIENCES_LISTINGS.filter((_, i) => i < 4).map(
                    (stay) => (
                      <ExperiencesCard key={stay.id} data={stay} />
                    )
                  )} */}
                </div>
                <div className="flex mt-11 justify-center items-center">
                  <ButtonSecondary>Próximamente</ButtonSecondary>
                </div>
              </Tab.Panel>
              <Tab.Panel className="">
                {/* <div className="mt-8 grid grid-cols-1 gap-6 md:gap-7 sm:grid-cols-2">
                  {DEMO_CAR_LISTINGS.filter((_, i) => i < 4).map((stay) => (
                    <CarCard key={stay.id} data={stay} />
                  ))}
                </div> */}
                <div className="flex mt-11 justify-center items-center">
                  <ButtonSecondary>Próximamente</ButtonSecondary>
                </div>
              </Tab.Panel>
            </Tab.Panels>
          </Tab.Group>
        </div>
      </div>
    );
  };

  return (
    <div className={`nc-AuthorPage ${className}`} data-nc-id="AuthorPage">
      <Helmet>
        <title>Bee || Mi Perfil</title>
      </Helmet>
      <main className="container mt-12 mb-24 lg:mb-32 flex flex-col lg:flex-row">
        <div className="block flex-grow mb-24 lg:mb-0">
          <div className="lg:sticky lg:top-24">{renderSidebar()}</div>
        </div>
        <div className="w-full lg:w-3/5 xl:w-2/3 space-y-8 lg:space-y-10 lg:pl-10 flex-shrink-0">
          {renderPurchases()}
          {errorMessage !== "" && (
            <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 bg-red-600 text-white px-6 py-3 rounded-2xl shadow-2xl transition-all duration-300 ease-in-out opacity-90 hover:opacity-100 hover:scale-105 flex flex-col gap-2">
              <p>{errorMessage}</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default ProfilePage;
